<script setup lang="ts">
import { computed, ref } from "vue";

/**
 * The QR code a trap administration row shows. The thumbnail is big enough to
 * read the module pattern, and clicking it opens a full-screen copy drawn at a
 * larger size — the qrcode endpoint renders at the requested pixel size, so the
 * expanded code is crisp rather than a stretched thumbnail.
 */
const props = withDefaults(
	defineProps<{
		src: string;
		alt: string;
		/** Thumbnail edge in pixels. */
		size?: number;
	}>(),
	{ size: 96 },
);

const expanded = ref(false);

const expandedSrc = computed(
	() => `${props.src}${props.src.includes("?") ? "&" : "?"}size=1024`,
);
</script>

<template>
	<div class="qr-code">
		<button
			type="button"
			class="qr-code-thumb"
			:aria-label="`Enlarge ${alt}`"
			:title="`Enlarge ${alt}`"
			@click="expanded = true"
		>
			<img :src="src" :alt="alt" :width="size" :height="size" />
		</button>

		<div
			v-if="expanded"
			class="modal fade show d-block"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			:aria-label="alt"
			@click.self="expanded = false"
			@keydown.esc="expanded = false"
		>
			<div class="modal-dialog modal-fullscreen">
				<div class="modal-content">
					<div
						class="modal-body d-flex flex-column align-items-center justify-content-center"
					>
						<img :src="expandedSrc" :alt="alt" class="qr-code-full" />
						<p class="small text-muted mt-3 mb-0">{{ alt }}</p>
					</div>
					<div class="modal-footer justify-content-center">
						<button
							type="button"
							class="btn btn-outline-secondary"
							@click="expanded = false"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="expanded" class="modal-backdrop fade show"></div>
	</div>
</template>

<style scoped>
.qr-code-thumb {
	padding: 0;
	border: 0;
	background: none;
	cursor: zoom-in;
}

/* The codes are drawn as sharp module blocks; never smooth them when scaled. */
.qr-code-thumb img,
.qr-code-full {
	image-rendering: pixelated;
}

.qr-code-full {
	width: min(80vw, 72vh);
	height: min(80vw, 72vh);
}
</style>
